import React, { useEffect, useMemo, useState } from 'react';
import Styles from './styles.module.css';
import Items from './items/index';
import { collection, doc, getDoc, getDocs, getFirestore } from 'firebase/firestore';
import SchoolJobPostings from '../9.items/2.jobPostings';
import { IoArrowDownOutline } from "react-icons/io5";
import { schoolOffersOf } from '../0.functions/offers';
import { useFitTable } from '../0.functions/useFitTable';
import Loading from '../9.items/loading/index';

type SchoolStudentSalonProps = {
  schoolId: string;
  hashTags: string[];
  filteredUsers: any[];
  usersLoaded: boolean;
};

const SchoolStudentSalon: React.FC<SchoolStudentSalonProps> = ({ schoolId, hashTags, filteredUsers, usersLoaded }) => {

  const db = getFirestore();

  const [ companyList, setCompanyList ] = useState<any[]>([]);
  const [ toggle, setToggle ] = useState({ type: "",  id: "", name: "" });
  const [ loading, setLoading ] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // filteredUsers が空なら即クリア
    if (!filteredUsers || filteredUsers.length === 0) {
      setCompanyList([]);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);

    const fetchSchool = async () => {
      // =============================
      // ② subcollection 取得
      // =============================
      const pvData: any[] = [];
      const visitsData: any[] = [];
      const applyData: any[] = [];

      await Promise.all(
        filteredUsers.map(async (user) => {
          const uid = user.id;
          if (!uid || cancelled) return;

          try {
            const [pvSnap, visitsSnap, applySnap] = await Promise.all([
              getDocs(collection(db, "users", uid, "pv")),
              getDocs(collection(db, "users", uid, "visits")),
              getDocs(collection(db, "users", uid, "apply")),
            ]);

            if (cancelled) return;

            pvSnap.forEach((d) =>
              pvData.push({ userId: uid, id: d.id, ...d.data() })
            );

            visitsSnap.forEach((d) =>
              visitsData.push({ userId: uid, id: d.id, ...d.data() })
            );

            applySnap.forEach((d) =>
              applyData.push({ userId: uid, id: d.id, ...d.data() })
            );
          } catch (e) {
            console.warn(`[SchoolStudentSalon] uid=${uid} の pv/visits/apply取得に失敗`, e);
          }
        })
      );

      if (cancelled) return;

      // =============================
      // ③ recruitId → company 情報取得
      // =============================
      const recruitIds = Array.from(
        new Set(
          [...pvData, ...visitsData, ...applyData]
            .map((v) => v.recruitId)
            .filter(Boolean)
            // 未登録サロンの手動追加(6.makeSalon.dart)ではrecruitIdに
            // 生徒が自由入力した店舗名がそのまま入るため、"/"を含む場合
            // doc()が想定外のネストしたパスとして解釈しpermission-deniedに
            // なる。そのようなIDはsearchIndex取得の対象から除外する。
            .filter((id: string) => !id.includes("/"))
        )
      );

      const recruitMap = new Map<
        string,
        {
          id: string;
          companyId: string;
          name: string;
          recruitStatus: boolean;
          jobPostingsStatus: boolean;
          pamphlet: boolean;
        }
      >();

      await Promise.all(
        recruitIds.map(async (recruitId) => {
          if (cancelled) return;

          try {
            const snap = await getDoc(doc(db, "searchIndex", recruitId));
            if (!snap.exists()) return;

            const data = snap.data();
            if (!data.companyId) return;

            recruitMap.set(recruitId, {
              id: snap.id,
              companyId: data.companyId,
              name: data.name ?? "",
              recruitStatus: data.recruitStatus ?? false,
              jobPostingsStatus: data.jobPostingsStatus ?? false,
              pamphlet: data.pamphlet ?? false,
            });
          } catch (e) {
            console.warn(`[SchoolStudentSalon] recruitId=${recruitId} のsearchIndex取得に失敗`, e);
          }
        })
      );

      if (cancelled) return;

      // =============================
      // ④ company 単位で集計
      // =============================
      const companyMap = new Map<
        string,
        {
          id: string;
          companyId: string;
          name: string;
          recruitStatus: boolean;
          jobPostingsStatus: boolean;
          pamphlet: boolean;
          pv: number;
          visits: number;
          favorite: number;
          apply: number;
          offer: number;
          decline: number;
          rejected: number;
        }
      >();

      recruitMap.forEach((r) => {
        if (!companyMap.has(r.companyId)) {
          companyMap.set(r.companyId, {
            id: r.id,
            companyId: r.companyId,
            name: r.name,
            recruitStatus: r.recruitStatus,
            jobPostingsStatus: r.jobPostingsStatus,
            pamphlet: r.pamphlet,
            pv: 0,
            visits: 0,
            favorite: 0,
            apply: 0,
            offer: 0,
            decline: 0,
            rejected: 0,
          });
        }
      });

      const ensureCompany = (data: any) => {
        const companyId =
          data.companyId ?? recruitMap.get(data.recruitId)?.companyId;
        if (!companyId) return null;
        return companyMap.get(companyId) ?? null;
      };

      // PV
      pvData.forEach((pv) => {
        const c = ensureCompany(pv);
        if (c) c.pv += 1;
      });

      // 見学
      visitsData.forEach((v) => {
        const c = ensureCompany(v);
        if (c) c.visits += 1;
      });

      // 内定承諾は、学生が登録しても学校が登録しても、サロンごとに1人1件として数える
      const offerUids = new Map<string, Set<string>>();
      const addOffer = (companyId: string, uid: string) => {
        if (!offerUids.has(companyId)) offerUids.set(companyId, new Set());
        offerUids.get(companyId)!.add(uid);
      };

      // 応募ステータス
      applyData.forEach((a) => {
        const c = ensureCompany(a);
        if (!c) return;

        switch (a.status) {
          case "志望先":
            c.favorite += 1;
            break;
          case "応募済み":
            c.apply += 1;
            break;
          case "内定承諾":
            addOffer(c.companyId, a.userId);
            break;
          case "辞退":
            c.decline += 1;
            break;
          case "不合格":
            c.rejected += 1;
            break;
          default:
            break;
        }
      });

      // 学校が登録した内定(memorii の求人から選んだもの)
      filteredUsers.forEach((u: any) => {
        schoolOffersOf(u, schoolId).forEach((o) => {
          if (o.companyId && companyMap.has(o.companyId)) addOffer(o.companyId, u.uid ?? u.id);
        });
      });
      offerUids.forEach((uids, companyId) => {
        const c = companyMap.get(companyId);
        if (c) c.offer = uids.size;
      });

      const nextCompanyList = Array.from(companyMap.values())
        .sort((a, b) => b.pv - a.pv)
        .map((row, index) => ({
          no: index + 1,
          ...row,
        }));

      if (!cancelled) {
        setCompanyList(nextCompanyList);
        setLoading(false);
      }
    };

    fetchSchool();

    return () => {
      cancelled = true;
    };
  }, [hashTags, filteredUsers, schoolId]);


  const [sortKey, setSortKey] = useState<SortKey>("pv");

  const displayList = useMemo(() => {

    if (!sortKey) return companyList;

    return [...companyList]
      .sort((a, b) => b[sortKey] - a[sortKey])
      .map((row, index) => ({
        ...row,
        no: index + 1,
      }));
  }, [companyList, sortKey]);

  // 表は画面の高さに合わせ、見出しを残して行だけをスクロールする
  const tableRef = useFitTable<HTMLDivElement>();

  return (
    <>

    <div className={Styles.table} ref={tableRef}>
      <div className={Styles.table_nav}>
        <h4 className={Styles.number}>No</h4>
        <h4 className={Styles.name}>企業名</h4>
        <div style={{ flex: "1" }}></div>
        <h4 className={Styles.items} onClick={() => sortKey === "pv" ? setSortKey("pv") : setSortKey("pv")}>
          閲覧数<IoArrowDownOutline className={Styles.order_icon} style={{ color: sortKey === "pv" ? "var(--mw-brand)" : undefined }} />
        </h4>
        <h4 className={Styles.items} onClick={() => sortKey === "visits" ? setSortKey("pv") : setSortKey("visits")}>
          サロン見学<IoArrowDownOutline className={Styles.order_icon} style={{ color: sortKey === "visits" ? "var(--mw-brand)" : undefined }} />
        </h4>
        <h4 className={Styles.items} onClick={() => sortKey === "favorite" ? setSortKey("pv") : setSortKey("favorite")}>
          志望先<IoArrowDownOutline className={Styles.order_icon} style={{ color: sortKey === "favorite" ? "var(--mw-brand)" : undefined }} />
        </h4>
        <h4 className={Styles.items} onClick={() => sortKey === "apply" ? setSortKey("pv") : setSortKey("apply")}>
          応募済み<IoArrowDownOutline className={Styles.order_icon} style={{ color: sortKey === "apply" ? "var(--mw-brand)" : undefined }} />
        </h4>
        <h4 className={Styles.items} onClick={() => sortKey === "offer" ? setSortKey("pv") : setSortKey("offer")}>
          内定承諾<IoArrowDownOutline className={Styles.order_icon} style={{ color: sortKey === "offer" ? "var(--mw-brand)" : undefined }} />
        </h4>
        <h4 className={Styles.items} onClick={() => sortKey === "decline" ? setSortKey("pv") : setSortKey("decline")}>
          辞退<IoArrowDownOutline className={Styles.order_icon} style={{ color: sortKey === "decline" ? "var(--mw-brand)" : undefined }} />
        </h4>
        <h4 className={Styles.items} onClick={() => sortKey === "rejected" ? setSortKey("pv") : setSortKey("rejected")}>
          不合格<IoArrowDownOutline className={Styles.order_icon} style={{ color: sortKey === "rejected" ? "var(--mw-brand)" : undefined }} />
        </h4>
        <h4 className={Styles.items}>求人票</h4>
        <h4 className={Styles.items}>パンフ</h4>
        <h4 className={Styles.items}>求人</h4>
      </div>

      <div className="schoolTableBody">
        {hashTags.length === 0 && (
          <p className={Styles.recruit_none}>
            コース、学年、クラスを選択してください。
          </p>
        )}

        {/* 学生のデータを受け取る前と、サロンごとの集計中は「読み込み中」を出す */}
        {hashTags.length > 0 && (!usersLoaded || loading) && (
          <Loading text="現在、統計を整理していますので、もうしばらくお待ちください。" />
        )}

        {hashTags.length > 0 && usersLoaded && !loading && displayList.length === 0 && (
          <p className={Styles.recruit_none}>
            該当する学生がいらっしゃいません。
          </p>
        )}

        {hashTags.length > 0 && usersLoaded && !loading && displayList.length > 0 && (
          <>
            {displayList.map((data) => (
              <Items
                key={data.companyId}
                data={data}
                setToggle={setToggle}
              />
            ))}
          </>
        )}
      </div>

    </div>

    {toggle.type === "jobPosting" ?
      <SchoolJobPostings schoolId={schoolId} setToggle={setToggle} recruitId={toggle.id} />
    :<></> }
    </>
  );
}

export default SchoolStudentSalon;

type SortKey =
  | "pv"
  | "visits"
  | "favorite"
  | "apply"
  | "offer"
  | "decline"
  | "rejected";